import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { TopBar } from "@/components/TopBar";
import { Button } from "@/components/ui/button";
import { Mail, Phone } from "lucide-react";

export default function ConsultationDetail() {
  const { t } = useApp();
  const { id } = useParams();
  const [c, setC] = useState(null);

  const load = () => api.get(`/consultations/${id}`).then((r) => setC(r.data));
  useEffect(() => { load(); }, [id]);

  const setStatus = async (status) => {
    await api.patch(`/consultations/${id}/status`, null, { params: { status } });
    toast.success(t("t_saved"));
    load();
  };

  if (!c) return <div className="flex min-h-screen items-center justify-center text-muted-foreground">…</div>;

  const isRiding = c.type === "riding_lesson";
  const pkg = c.package === "ten_pack" ? t("pkg_ten") : c.package === "single" ? t("pkg_single") : null;
  const row = (label, val) =>
    val ? (
      <div className="flex flex-col gap-0.5 border-b border-border py-3 sm:flex-row sm:justify-between">
        <span className="text-sm text-muted-foreground">{label}</span>
        <span className="text-foreground sm:text-right">{val}</span>
      </div>
    ) : null;

  return (
    <div className="min-h-screen">
      <TopBar showBack backTo="/admin" showLogout />
      <div className="mx-auto max-w-2xl px-4 py-8 sm:px-6">
        <div className="rounded-[20px] border border-border bg-card p-6 shadow-[0_8px_30px_rgba(62,97,72,0.06)]">
          <div className="flex flex-wrap items-center gap-2">
            <span className={`rounded-full px-3 py-0.5 text-xs font-medium ${isRiding ? "bg-[#EAF0EB] text-primary" : "bg-[#FBF3E7] text-[#8a6d1f]"}`}>
              {isRiding ? t("type_riding") : t("type_consultation")}
            </span>
            <span className="text-xs text-muted-foreground">
              {{ new: t("status_new"), reviewed: t("status_reviewed"), closed: t("status_closed") }[c.status]}
            </span>
          </div>
          <h1 className="mt-3 font-serif-h text-3xl text-foreground" data-testid="request-name">{c.name}</h1>

          <div className="mt-4 flex flex-wrap gap-3">
            <a data-testid="contact-email" href={`mailto:${c.email}`} className="inline-flex items-center gap-2 rounded-full bg-[#EAF0EB] px-4 py-2 text-sm text-primary hover:opacity-80">
              <Mail className="h-4 w-4" strokeWidth={1.5} />{c.email}
            </a>
            {c.phone && (
              <a data-testid="contact-phone" href={`tel:${c.phone}`} className="inline-flex items-center gap-2 rounded-full bg-[#EAF0EB] px-4 py-2 text-sm text-primary hover:opacity-80">
                <Phone className="h-4 w-4" strokeWidth={1.5} />{c.phone}
              </a>
            )}
          </div>

          <div className="mt-6">
            {row(t("type"), isRiding ? t("type_riding") : t("type_consultation"))}
            {row(t("package"), pkg)}
            {row(t("horse_name"), c.horse_name)}
            {row(t("preferred_date"), c.preferred_date)}
            {row(t("case_description"), c.case_description)}
          </div>

          <div className="mt-6 flex flex-wrap gap-3">
            <Button data-testid="mark-reviewed" onClick={() => setStatus("reviewed")} variant="outline" className="rounded-full">
              {t("mark_reviewed")}
            </Button>
            <Button data-testid="mark-closed" onClick={() => setStatus("closed")} className="rounded-full bg-primary text-primary-foreground hover:bg-[#2C4633]">
              {t("mark_closed")}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
